import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { apiClient } from '../../api/client';
import { useApi } from '../../api/useApi';
import { Modal } from '../Modal';

import { FilterAccordion, type FilterOption } from './FilterAccordion';

/** Mirrors the query parameters GET /offers accepts. */
export interface JobFilters {
  pavilionIds: number[];
  categoryIds: number[];
  standIds: number[];
  locations: string[];
}

export const EMPTY_JOB_FILTERS: JobFilters = {
  pavilionIds: [],
  categoryIds: [],
  standIds: [],
  locations: [],
};

export interface JobFiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: JobFilters;
  onChange: (filters: JobFilters) => void;
}

export function JobFiltersModal({
  open,
  onClose,
  filters,
  onChange,
}: JobFiltersModalProps): ReactElement {
  const { t } = useTranslation();

  // Options come from the API (GET /offers/filters), which returns only what
  // this fair actually has rather than a global taxonomy.
  const filtersRequest = useApi(() => apiClient.getOfferFilters());

  const options = useMemo(() => {
    const available = filtersRequest.data ?? {
      pavilions: [],
      categories: [],
      locations: [],
      stands: [],
    };

    return {
      pavilions: available.pavilions.map((pavilion): FilterOption => ({
        id: String(pavilion.id),
        label: pavilion.name,
      })),
      categories: available.categories.map((category): FilterOption => ({
        id: String(category.id),
        label: category.name,
      })),
      locations: available.locations.map((location): FilterOption => ({
        id: location,
        label: location,
      })),
      stands: available.stands.map((stand): FilterOption => ({
        id: String(stand.id),
        label: stand.title,
      })),
    };
  }, [filtersRequest.data]);

  const toggleIds = (key: 'pavilionIds' | 'categoryIds' | 'standIds', id: string): void => {
    const numeric = Number(id);
    const current = filters[key];

    onChange({
      ...filters,
      [key]: current.includes(numeric)
        ? current.filter((value) => value !== numeric)
        : [...current, numeric],
    });
  };

  const toggleLocation = (location: string): void => {
    onChange({
      ...filters,
      locations: filters.locations.includes(location)
        ? filters.locations.filter((value) => value !== location)
        : [...filters.locations, location],
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('filters.title')}
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              onChange(EMPTY_JOB_FILTERS);
            }}
            className="flex-1 rounded-full border border-fog-500 py-3 text-fog-100"
          >
            {t('filters.reset')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full bg-primary py-3 font-semibold text-white"
          >
            {t('filters.apply')}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <FilterAccordion
          title={t('filters.categories')}
          options={options.pavilions}
          selected={filters.pavilionIds.map(String)}
          onToggle={(id) => {
            toggleIds('pavilionIds', id);
          }}
        />
        <FilterAccordion
          title={t('filters.positions')}
          options={options.categories}
          selected={filters.categoryIds.map(String)}
          onToggle={(id) => {
            toggleIds('categoryIds', id);
          }}
        />
        <FilterAccordion
          title={t('filters.locations')}
          options={options.locations}
          selected={filters.locations}
          onToggle={toggleLocation}
        />
        <FilterAccordion
          title={t('filters.companies')}
          options={options.stands}
          selected={filters.standIds.map(String)}
          onToggle={(id) => {
            toggleIds('standIds', id);
          }}
        />
      </div>
    </Modal>
  );
}
